/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/LeaveRequestModern.tsx
 * PURPOSE: Staff self-service leave request page — submit, view, cancel, balance.
 *          2026-08-11: fixed 3 P0 route-drift bugs (list/balance/cancel all called
 *          stale backend routes) by moving off hand-rolled fetch onto a centralized
 *          service (services/api/leaveRequest.ts); added DRAFT/MANAGER_REVIEW cancel
 *          eligibility (G4); route guard added at App.tsx (was previously unguarded).
 *          2026-08-15: web-sync checklist A.2 — added the shared
 *          EmployeeBottomNav (this route previously had no bottom nav at all).
 *          2026-08-17: BS-301 Route 6 §1 gaps — (1) EMPTY-BALANCE gate: "Gửi
 *          đơn" is now aria-disabled with a visible reason when the selected
 *          leave type's remaining balance is 0 (previously the form let the
 *          employee submit regardless and relied entirely on a server 4xx);
 *          (2) submit is also blocked when the balance fetch itself errored
 *          ("KHÔNG cho gửi đơn khi thiếu dữ liệu số dư" — submitting without
 *          knowing the real balance risks a request the employee believes
 *          was balance-checked but wasn't).
 *          2026-09-04 (mobile-app-shell parity pass): this route was still
 *          rendering `styles` imported from `./ShiftSwapModern.module.css` —
 *          a DIFFERENT feature's stylesheet (max-width:1400px desktop layout,
 *          confirmed via grep), reused here only because both pages started
 *          from the same early scaffold. That file is also imported by
 *          KPISelfAssessment.tsx, KPIManagerReview.tsx and TrainingPortal.tsx
 *          (grep-verified) — KPIManagerReview is a MANAGER-persona surface
 *          out of this pass's employee-bottom-nav scope, so editing the
 *          shared file in place risked changing layout for pages nobody
 *          asked to touch. Switched to a dedicated
 *          `LeaveRequestModern.module.css` (480px app-shell `.page`,
 *          single-row icon-back header, AppButton-parity buttons, NQH design
 *          tokens instead of ShiftSwapModern.module.css's hardcoded hex) —
 *          see that file's own header for the full blast-radius reasoning.
 *          `ShiftSwapModern.tsx`/`.module.css` themselves were left
 *          untouched (separate page, separate route, not part of this
 *          session's employee-history/leave-request scope).
 *          Kept the existing single-route list+inline-form structure (mobile
 *          splits list/form into 2 screens — LeaveScreen.tsx +
 *          LeaveRequestFormScreen.tsx — but this page's inline-reveal form is
 *          a reasonable web adaptation of that same flow, not a structural
 *          mismatch worth a route split for this narrowing pass); the header
 *          `style={{ paddingBottom: ... }}` inline override is gone since
 *          `.page` in the new dedicated stylesheet already owns that
 *          padding (no longer shared with pages that shouldn't get it).
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-04
 * COMPLIANCE: SDLC 6.5.0 + BS-301-PHAN-DOAN-C-NHAN-VIEN-WEB-REDESIGN §Route 6
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management, Employee Self-Service Leave
 * - Impact: Revenue Critical — Leave balance enforcement, Vietnamese Labor Law 2019
 * - Stakeholders: Service Staff (NQH), Branch Managers, HR Team
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Page Components
 * - Pattern: React Router, Page Layout, Component Composition
 * - Dependencies: React 18, React Router, TypeScript, services/api/leaveRequest
 *
 * @cross-references
 * - Design: docs/01-planning/02-uat-len-lich-cham-cong/BS-301-PHAN-DOAN-C-NHAN-VIEN-WEB-REDESIGN.md
 * - Design: docs/02-design/05-business-logic/LEAVE-MANAGEMENT-DESIGN.md
 * - API: backend/app/api/v1/leave_request.py (BR-LEAVE-001 v2.1.0)
 * - Service: frontend/src/services/api/leaveRequest.ts
 * - Styles (dedicated, split off from ShiftSwapModern.module.css): frontend/src/pages/LeaveRequestModern.module.css
 * - Mobile source (list/form 2-screen split, kept as inline-form on web): mobile/src/screens/LeaveScreen.tsx, mobile/src/screens/LeaveRequestFormScreen.tsx
 * - Tests: frontend/tests/services/leaveRequest.test.ts, frontend/tests/pages/LeaveRequestModern.test.tsx
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { EmployeeBottomNav } from '../components/navigation/EmployeeBottomNav';
import styles from './LeaveRequestModern.module.css';
import {
  getMyLeaveRequests,
  getMyLeaveBalance,
  getMyCompensatoryBalance,
  getPublicHolidays,
  createLeaveRequest as createLeaveRequestApi,
  cancelLeaveRequest as cancelLeaveRequestApi,
  type LeaveRequestDTO,
  type LeaveBalanceDTO,
  type CompensatoryBalanceDTO,
  type PublicHolidayDTO,
} from '../services/api/leaveRequest';
import { computeLeavePreview, formatLeavePreview } from '../utils/leaveWorkingDays';
import { toLocalISODate } from '@/utils/date';

type LeaveRequest = LeaveRequestDTO;
type LeaveBalance = LeaveBalanceDTO;
type CompensatoryBalance = CompensatoryBalanceDTO;
type PublicHoliday = PublicHolidayDTO;

/** BS-024-R15/R10: giá trị nghỉ bù — tách riêng khỏi 4 loại "thường" trong LEAVE_TYPES. */
const COMPENSATORY_TYPE = 'compensatory';

/** DD/MM/YYYY từ chuỗi ISO YYYY-MM-DD (không phụ thuộc timezone parsing của Date). */
function formatDmy(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

// Backend allows cancel from draft, submitted, or manager_review
// (backend/app/api/v1/leave_request.py:828-830) — the cancel button + status
// label must cover all 3, not just SUBMITTED (G4 fix, same batch as the P0
// endpoint/method fixes since it was discovered verifying the same endpoint).
//
// ISS-EMP-4 fix (2026-08-16): this list — and STATUS_LABELS below — used
// SCREAMING-case keys, but `LeaveStatus` (backend/app/models/leave_request.py:58-77)
// is a lowercase-value enum and `_build_response()`
// (backend/app/api/v1/leave_request.py:236-248) passes `lr.status` through
// unchanged. Every lookup against these SCREAMING keys silently missed on real
// API data: the badge fell through to the raw enum string, and this list never
// matched `req.status` so the "Hủy đơn" button never rendered even for
// cancel-eligible requests. Canonical VI labels + full 7-value enum registered
// in docs/02-design/04-ui-ux-design/UI-LABELS-VI-ENUM.md §8b (added same PR).
const CANCELLABLE_STATUSES = ['draft', 'submitted', 'manager_review'];

const STATUS_LABELS: Record<string, string> = {
  draft: 'Bản nháp',
  submitted: 'Chờ duyệt',
  manager_review: 'Quản lý đang xét',
  auto_approved: 'Tự động duyệt',
  approved: 'Đã duyệt',
  rejected: 'Từ chối',
  cancelled: 'Đã hủy',
};

// BS-024-R03: nhãn hiển thị cho MỌI leave_type server chấp nhận (kể cả
// public_holiday/compensatory) — dùng để hiển thị đúng nhãn cho đơn LỊCH SỬ,
// bất kể loại đó còn là chip chọn được hay không (public_holiday KHÔNG còn là
// chip từ BS-024-R03, nhưng đơn cũ/đơn tạo qua API khác vẫn có thể mang giá
// trị này — server vẫn hợp lệ nó, xem annotation [6]).
const LEAVE_TYPE_LABELS: Record<string, string> = {
  annual: 'Nghỉ phép năm',
  public_holiday: 'Nghỉ lễ',
  wedding: 'Nghỉ kết hôn',
  funeral: 'Nghỉ tang gia',
  child_wedding: 'Nghỉ con kết hôn',
  compensatory: 'Nghỉ bù',
};

// BS-024-R03: ĐÚNG 5 chip chọn được trên form — public_holiday bị loại khỏi
// UI có chủ đích (server vẫn hợp lệ giá trị này ở tầng API, xem annotation
// [6] — đây KHÔNG phải lỗi, là quyết định thiết kế v0).
const LEAVE_TYPES = [
  { value: 'annual', label: 'Nghỉ phép năm' },
  { value: 'wedding', label: 'Nghỉ kết hôn' },
  { value: 'funeral', label: 'Nghỉ tang lễ' },
  { value: 'child_wedding', label: 'Nghỉ cưới con' },
  { value: COMPENSATORY_TYPE, label: 'Nghỉ bù' },
];

function futureDate(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toLocalISODate(d);
}

function formatSlaDeadline(slaDeadline: string | undefined, isEmergency: boolean): string {
  if (!slaDeadline) return '';
  const slaHours = isEmergency ? 4 : 24;
  return isEmergency ? `SLA: ${slaHours} giờ (Khẩn cấp)` : `SLA: ${slaHours} giờ`;
}

export const LeaveRequestModern: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // If URL is /leave-requests/balance, show balance-only view
  const isBalancePage = location.pathname.endsWith('/balance');
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  // Derived from URL — no state needed; routing drives visibility
  const showBalance = isBalancePage;
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  // BS-024-R10/R11/§2b — số dư nghỉ bù còn dùng được (card [2b] + dropdown [13])
  const [compBalances, setCompBalances] = useState<CompensatoryBalance[]>([]);
  const [compBalanceError, setCompBalanceError] = useState<string | null>(null);
  const [compBalanceLoaded, setCompBalanceLoaded] = useState(false);

  // BS-024-R08/R09/[10] — ngày nghỉ lễ theo năm, cache theo năm để phục vụ cả
  // preview [11] (năm của start/end đang chọn) lẫn section tham chiếu [10]
  // (năm hiện tại) mà không gọi lại API cho cùng 1 năm nhiều lần.
  const [holidaysByYear, setHolidaysByYear] = useState<Record<number, PublicHoliday[]>>({});
  const [holidaysError, setHolidaysError] = useState<string | null>(null);

  // Form fields
  const [leaveType, setLeaveType] = useState('annual');
  const [startDate, setStartDate] = useState(futureDate(5));
  const [endDate, setEndDate] = useState(futureDate(5));
  const [reason, setReason] = useState('');
  // BS-024-R10/[13]: khoản nghỉ bù đã chọn — chỉ có ý nghĩa khi leaveType === compensatory
  const [compensatoryBalanceId, setCompensatoryBalanceId] = useState<string | null>(null);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const items = await getMyLeaveRequests();
      setRequests(items);
      setListError(null);
    } catch {
      // Pattern #15 (@cto 2026-08-21, PR #625 condition 1): getMyLeaveRequests
      // now THROWS on !ok — surface a real error state instead of swallowing
      // the failure into an empty list ("empty giả"). Same contract as
      // loadBalance/balanceError below.
      setListError('Không thể tải danh sách đơn nghỉ phép. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBalance = useCallback(async () => {
    try {
      const items = await getMyLeaveBalance();
      setBalances(items);
      setBalanceError(null);
    } catch {
      // FIX (G2): the old inline fetch's catch block set NOTHING at all on
      // error, leaving the balance card stuck forever on "Đang tải số dư..."
      // (indistinguishable from a slow network). Surface a real error state.
      setBalanceError('Không thể tải số dư nghỉ phép. Vui lòng thử lại.');
    }
  }, []);

  // BS-024-R10/§2b: số dư nghỉ bù còn dùng được. Cùng contract throw-on-!ok
  // với loadBalance — lỗi fetch thật không được lẫn với "chưa có ngày nghỉ
  // bù nào" (2 trạng thái khác nhau, xem card [2b]).
  const loadCompBalance = useCallback(async () => {
    try {
      const items = await getMyCompensatoryBalance();
      setCompBalances(items);
      setCompBalanceError(null);
    } catch {
      setCompBalanceError('Không thể tải số dư nghỉ bù. Vui lòng thử lại.');
    } finally {
      setCompBalanceLoaded(true);
    }
  }, []);

  // BS-024-R08/R09/[10]: nạp ngày nghỉ lễ cho 1 năm, cache lại — dùng chung
  // cho preview [11] (năm của start/end) và section tham chiếu [10] (năm
  // hiện tại). Không nạp lại nếu năm đó đã có trong cache.
  const ensureHolidaysForYear = useCallback((year: number) => {
    setHolidaysByYear(prev => {
      if (year in prev) return prev; // đã có (kể cả mảng rỗng hợp lệ) — không gọi lại
      // Đánh dấu "đang nạp" bằng cách không set state ở đây — gọi API async bên dưới.
      return prev;
    });
    getPublicHolidays(year)
      .then(items => {
        setHolidaysByYear(prev => (year in prev ? prev : { ...prev, [year]: items }));
        setHolidaysError(null);
      })
      .catch(() => {
        setHolidaysError('Không thể tải danh sách ngày nghỉ lễ. Vui lòng thử lại.');
      });
  }, []);

  useEffect(() => {
    loadRequests();
    loadBalance();
    loadCompBalance();
  }, [loadRequests, loadBalance, loadCompBalance]);

  const currentYear = new Date().getFullYear();

  // Section [10] "Ngày nghỉ lễ trong năm {YYYY}" tham chiếu năm hiện tại — nạp
  // khi mở form (chỉ cần khi đang xem loại THƯỜNG, nhưng nạp sớm để sẵn khi
  // đổi chip không phải chờ).
  useEffect(() => {
    if (showForm) ensureHolidaysForYear(currentYear);
  }, [showForm, currentYear, ensureHolidaysForYear]);

  // Preview [11]: nạp thêm năm của start/end đang chọn nếu khác năm hiện tại
  // (vd đơn vắt qua năm sau).
  useEffect(() => {
    if (!showForm || leaveType === COMPENSATORY_TYPE) return;
    const startYear = Number(startDate.slice(0, 4));
    const endYear = Number(endDate.slice(0, 4));
    if (Number.isFinite(startYear)) ensureHolidaysForYear(startYear);
    if (Number.isFinite(endYear) && endYear !== startYear) ensureHolidaysForYear(endYear);
  }, [showForm, leaveType, startDate, endDate, ensureHolidaysForYear]);

  // BS-024-R09: preview client-side, KHÔNG gửi lên server — server tự tính
  // lại theo đúng công thức này (_count_working_days, BS-024-R08).
  const leavePreview = useMemo(() => {
    const startYear = Number(startDate.slice(0, 4));
    const endYear = Number(endDate.slice(0, 4));
    const holidays = [
      ...(holidaysByYear[startYear] ?? []),
      ...(Number.isFinite(endYear) && endYear !== startYear ? holidaysByYear[endYear] ?? [] : []),
    ];
    return computeLeavePreview(startDate, endDate, holidays);
  }, [startDate, endDate, holidaysByYear]);

  // BS-301 Route 6 §1 (2026-08-17) — EMPTY-BALANCE gate + "missing balance
  // data" gate. Only gates on a CONFIRMED 0 balance for the currently
  // selected leave type — an employee who hasn't submitted the form for a
  // leave type with no balance record at all (e.g. a type this tenant
  // doesn't track separately) is not blocked here; the backend remains the
  // final authority (see "Vượt quota" Gherkin scenario — server-side reject
  // with a specific inline message either way).
  const selectedBalance = balances.find(b => b.leave_type === leaveType);
  const balanceDataMissing = balanceError !== null;
  const selectedBalanceIsZero = !!selectedBalance && selectedBalance.remaining_days === 0;
  const isCompensatory = leaveType === COMPENSATORY_TYPE;

  // BS-024-R06 (loại thường): `start_date` hợp lệ + không quá khứ;
  // `end_date >= start_date`. `<input type="date">` chỉ cho phép rỗng hoặc
  // YYYY-MM-DD hợp lệ ở tầng browser nên "không hợp lệ" thực tế chỉ là rỗng
  // ở đây; ISO YYYY-MM-DD so sánh được trực tiếp bằng string (fixed-width).
  // Cùng thông báo VI với mobile (LeaveRequestFormScreen.tsx validate()).
  const todayIsoStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const dateValidationError = isCompensatory
    ? null
    : !startDate
      ? 'Ngày bắt đầu không hợp lệ.'
      : !endDate
        ? 'Ngày kết thúc không hợp lệ.'
        : startDate < todayIsoStr
          ? 'Không thể chọn ngày bắt đầu trong quá khứ.'
          : endDate < startDate
            ? 'Ngày kết thúc phải sau hoặc bằng ngày bắt đầu.'
            : null;

  // BS-024-R04/R05 (loại thường) + BS-024-R10/R11 (nghỉ bù — [13] rỗng/lỗi
  // chặn Gửi đơn tương đương, không có khoản chọn cũng chặn).
  const submitBlockedReason = isCompensatory
    ? (compBalanceError !== null
        ? 'Không kiểm tra được số dư nghỉ bù — vui lòng thử lại sau.'
        : compBalanceLoaded && compBalances.length === 0
          ? 'Không có ngày nghỉ bù khả dụng'
          : !compensatoryBalanceId
            ? 'Vui lòng chọn khoản nghỉ bù.'
            : null)
    : balanceDataMissing
      ? 'Không thể xác nhận số dư phép — vui lòng thử lại trước khi gửi đơn.'
      : selectedBalanceIsZero
        ? `Số dư "${LEAVE_TYPE_LABELS[leaveType] || leaveType}" hiện là 0 ngày — không thể gửi đơn cho loại nghỉ này.`
        : dateValidationError
          ? dateValidationError
          : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // BS-301 Route 6 §1 (2026-08-17) — defense in depth: the submit button is
    // already aria-disabled for these 2 cases, but a form can still be
    // submitted via Enter-key on some browsers/AT even with a disabled
    // button, so re-check here before calling the API.
    if (submitBlockedReason) {
      setError(submitBlockedReason);
      return;
    }
    setSubmitting(true);
    setError(null);
    setMessage(null);

    try {
      // BS-024-R15/DD-0115 §2.5: nghỉ bù KHÔNG gửi start_date/end_date (server
      // tự lấy earned_from của khoản đã chọn) và PHẢI gửi compensatory_balance_id;
      // loại thường thì ngược lại — gửi cả 2 ngày, KHÔNG gửi compensatory_balance_id.
      const body = isCompensatory
        ? {
            leave_type: leaveType,
            reason: reason || undefined,
            compensatory_balance_id: compensatoryBalanceId || undefined,
          }
        : {
            leave_type: leaveType,
            start_date: startDate,
            end_date: endDate,
            reason: reason || undefined,
          };

      const { ok, data } = await createLeaveRequestApi(body);

      if (ok) {
        const lr: LeaveRequest = data.leave_request || data;
        const isAutoApproved = data.auto_approved === true || lr.status === 'APPROVED';
        const isEmergency = lr.is_emergency;
        const slaHours = isEmergency ? 4 : 24;

        let successMsg: string;
        if (isAutoApproved) {
          successMsg = `Yêu cầu nghỉ phép đã được tự động duyệt thành công. Đã duyệt.`;
        } else {
          const slaPart = isEmergency
            ? `SLA: 4 giờ (Khẩn cấp)`
            : `SLA: ${slaHours} giờ`;
          successMsg = `Yêu cầu nghỉ phép đã được gửi thành công. Chờ duyệt. ${slaPart}.`;
        }

        setMessage(successMsg);
        setShowForm(false);
        setReason('');
        setLeaveType('annual');
        setStartDate(futureDate(5));
        setEndDate(futureDate(5));
        setCompensatoryBalanceId(null);

        // Prepend to list
        if (lr && lr.id) {
          setRequests(prev => [lr, ...prev]);
        } else {
          await loadRequests();
        }
        // Refresh balance — 3 query "keys" theo wireframe NAVIGATION:
        // myRequests (trên), balance, compensatoryBalance (BS-024-R11 —
        // khoản vừa dùng phải biến mất khỏi card [2b]/dropdown [13] ngay).
        await Promise.all([loadBalance(), loadCompBalance()]);
      } else {
        const rawDetail = data?.detail;
        const errMsg =
          (typeof rawDetail === 'string' ? rawDetail : rawDetail?.message_vi || rawDetail?.message) ||
          data?.message_vi ||
          data?.message ||
          'Không thể gửi yêu cầu nghỉ phép.';
        setError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
      }
    } catch {
      setError('Lỗi kết nối. Vui lòng thử lại.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (requestId: string) => {
    try {
      const { ok, data } = await cancelLeaveRequestApi(requestId);
      if (ok) {
        setMessage('Yêu cầu nghỉ phép đã được hủy thành công. Đã hủy.');
        // ISS-EMP-4 (2026-08-16): was 'CANCELLED' (uppercase) — matched the old
        // buggy SCREAMING-case STATUS_LABELS key, so the badge accidentally
        // never noticed this mismatch was wrong. Now that STATUS_LABELS uses
        // the real lowercase LeaveStatus values, this optimistic local update
        // must match too, or the badge would show the raw string "CANCELLED"
        // instead of "Đã hủy" right after a successful cancel.
        setRequests(prev =>
          prev.map(r => r.id === requestId ? { ...r, status: 'cancelled' } : r)
        );
        // BS-024-R16 (refund-on-cancel): nếu đơn vừa hủy là nghỉ bù, khoản
        // được hoàn lại phía server trong cùng transaction — refetch cả 2 để
        // card [2b] phản ánh đúng ngay (loadBalance no-op vô hại nếu đơn
        // không phải nghỉ bù).
        await Promise.all([loadBalance(), loadCompBalance()]);
      } else {
        const rawDetail = data?.detail;
        const errMsg =
          (typeof rawDetail === 'string' ? rawDetail : rawDetail?.message_vi) ||
          data?.message_vi ||
          'Không thể hủy yêu cầu.';
        setError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
      }
    } catch {
      setError('Lỗi kết nối. Vui lòng thử lại.');
    }
  };

  return (
    // 2026-09-04 (mobile-app-shell parity): `.page` in the now-dedicated
    // LeaveRequestModern.module.css owns padding-bottom for the
    // EmployeeBottomNav directly — no more inline style override needed
    // (that was only there because the old shared ShiftSwapModern.module.css
    // couldn't be edited without affecting unrelated pages).
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          {/* 2026-09-04 (BUG THẬT, báo cáo live): nút này từng trỏ `/dashboard`
              khiến nhân viên bị đẩy thẳng ra màn ĐĂNG NHẬP — `/dashboard` là
              `<DashboardGuard />` (App.tsx:405), không phải trang nhân viên.
              Đích đúng là `/dashboard/staff` (App.tsx:438) — cùng đích 3 màn
              anh em đã dùng: AttendanceHistory.tsx:84, ProfilePage.tsx:229,
              Utilities.tsx:218. */}
          <button
            type="button"
            onClick={() => navigate('/dashboard/staff')}
            className={styles.headerBackButton}
            aria-label="Quay lại"
            data-testid="leave-request-header-back"
          >
            <ChevronLeft size={24} aria-hidden="true" />
          </button>
          <div className={styles.headerTitleWrap}>
            <h1 id="leave-request-title" className={styles.title}>Nghỉ Phép</h1>
          </div>
        </div>
      </header>

      <main aria-labelledby="leave-request-title" className={styles.main}>

        {/* Actions row */}
        <div className={styles.actionsRow}>
          <button
            onClick={() => { setShowForm(!showForm); setError(null); setMessage(null); }}
            className={styles.primaryButton}
            data-testid="create-leave"
          >
            {showForm ? 'Đóng form' : 'Tạo đơn'}
          </button>
          <button
            onClick={() => {
              if (isBalancePage) {
                navigate('/leave-requests');
              } else {
                navigate('/leave-requests/balance');
              }
            }}
            className={styles.secondaryButton}
            data-testid="leave-balance"
          >
            {isBalancePage ? 'Danh sách đơn' : 'Số dư'}
          </button>
        </div>

        {/* Status messages */}
        {message && (
          <p
            className={styles.statusSuccess}
            data-testid="leave-success-msg"
            role="alert"
            aria-live="polite"
          >
            {message}
          </p>
        )}
        {error && (
          <p className={styles.statusError} data-testid="leave-error-msg">{error}</p>
        )}

        {/* Balance section */}
        {showBalance && (
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Số dư nghỉ phép</h2>
            {balanceError ? (
              <p className={styles.statusError} data-testid="leave-balance-error">{balanceError}</p>
            ) : balances.length === 0 ? (
              <p className={styles.cardDescription}>Đang tải số dư...</p>
            ) : (
              <ul className={styles.requestList}>
                {balances.map(b => (
                  <li key={b.leave_type} className={styles.requestItem} data-testid={`balance-${b.leave_type}`}>
                    <div className={styles.requestHeader}>
                      {/* Balance rows aren't a request-status field — badge just
                          borrows the "approved" (green/positive) visual per
                          the new lowercase-status CSS selectors (ISS-EMP-4
                          convention: real LeaveStatus values are lowercase;
                          previously hardcoded "APPROVED" uppercase, which only
                          ever matched the old shared ShiftSwapModern.module.css
                          by coincidence). */}
                      <span className={styles.statusBadge} data-status="approved">
                        {LEAVE_TYPE_LABELS[b.leave_type] || b.leave_type.toUpperCase()}
                      </span>
                      <span className={styles.requestDate}>
                        Còn lại: <strong>{b.remaining_days}</strong> / {b.total_days} ngày phép
                      </span>
                    </div>
                    <p className={styles.requestReason}>
                      Đã dùng: {b.used_days} ngày
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* [2b] Card "Số ngày nghỉ bù" — cùng nhóm điều kiện hiển thị với card
            [2] (showBalance/isBalancePage) — xem ghi chú ở đầu file về khác
            biệt AS-BUILT (toggle danh sách/số dư) so với wireframe (merged
            single-view); giữ nguyên cấu trúc điều hướng hiện có, chỉ bổ sung
            NỘI DUNG số dư nghỉ bù theo đúng vị trí "card số dư" đã có. */}
        {showBalance && (
          <div className={styles.card} data-testid="comp-balance-card">
            <h2 className={styles.cardTitle}>Số ngày nghỉ bù</h2>
            {compBalanceError ? (
              <p className={styles.statusError} data-testid="comp-balance-error">{compBalanceError}</p>
            ) : !compBalanceLoaded ? (
              <p className={styles.cardDescription}>Đang tải số dư nghỉ bù...</p>
            ) : compBalances.length === 0 ? (
              <p className={styles.cardDescription} data-testid="comp-balance-empty">
                Chưa có ngày nghỉ bù nào
              </p>
            ) : (
              <ul className={styles.compBalanceList}>
                {compBalances.map(cb => (
                  <li key={cb.id} className={styles.compBalanceItem} data-testid={`comp-balance-${cb.id}`}>
                    <span>{cb.earned_occasion} — {cb.remaining_days} ngày</span>
                    <span className={styles.compBalanceExpiry}>Hết hạn {formatDmy(cb.expiry_date)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* New leave request form */}
        {showForm && (
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Đơn xin nghỉ phép mới</h2>
            <form onSubmit={handleSubmit} className={styles.form}>
              {/* [6] Chip chọn loại nghỉ phép — BS-024-R03: đúng 5 loại, thay
                  cho <select> cũ để hiện đúng layout wireframe (public_holiday
                  bị loại khỏi UI có chủ đích). Giữ data-testid="leave-type-select"
                  trên container (không còn là thẻ <select> thật) để không phá
                  test hiện có đang chỉ assert "field vẫn hiện". */}
              <div className={styles.formGroup}>
                <label className={styles.label} id="leave-type-label">Loại nghỉ phép</label>
                <div
                  className={styles.chipGroup}
                  role="radiogroup"
                  aria-labelledby="leave-type-label"
                  data-testid="leave-type-select"
                >
                  {LEAVE_TYPES.map(lt => (
                    <button
                      key={lt.value}
                      type="button"
                      role="radio"
                      aria-checked={leaveType === lt.value}
                      className={`${styles.chip} ${leaveType === lt.value ? styles.chipSelected : ''}`}
                      onClick={() => {
                        setLeaveType(lt.value);
                        setCompensatoryBalanceId(null);
                      }}
                      data-testid={`leave-type-chip-${lt.value}`}
                    >
                      {lt.label}
                    </button>
                  ))}
                </div>
              </div>

              {!isCompensatory ? (
                <>
                  {/* [7]/[8] Both date inputs in ONE div so :first-of-type /
                      :last-of-type / :nth-of-type(2) selectors in the test
                      resolve correctly relative to this container */}
                  <div className={styles.formGroup}>
                    <label htmlFor="leave-start-date" className={styles.label}>
                      Ngày bắt đầu
                    </label>
                    <input
                      id="leave-start-date"
                      type="date"
                      name="start_date"
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      required
                      className={styles.input}
                      data-testid="leave-start-date"
                    />
                    <label htmlFor="leave-end-date" className={styles.label} style={{ marginTop: '12px' }}>
                      Ngày kết thúc
                    </label>
                    <input
                      id="leave-end-date"
                      type="date"
                      name="end_date"
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      required
                      className={styles.input}
                      data-testid="leave-end-date"
                    />
                  </div>

                  {/* [11] Preview "Số ngày phép sẽ trừ" — BS-024-R09, client-side
                      only, KHÔNG gửi giá trị này lên server. */}
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Số ngày phép sẽ trừ</label>
                    <p className={styles.previewBox} data-testid="leave-working-days-preview">
                      {formatLeavePreview(leavePreview)}
                    </p>
                  </div>

                  {/* [10] Section "Ngày nghỉ lễ trong năm {YYYY}" — tiêu đề
                      clickable → mở SC-032-EMP-PUBLIC-HOLIDAYS. */}
                  <button
                    type="button"
                    className={styles.holidayLinkSection}
                    onClick={() => navigate('/public-holidays')}
                    data-testid="leave-holidays-link"
                  >
                    <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      Ngày nghỉ lễ trong năm {currentYear}
                      <ChevronRight size={16} aria-hidden="true" />
                    </span>
                    {!holidaysError && (holidaysByYear[currentYear] ?? []).length > 0 && (
                      <span style={{ display: 'block', marginTop: '8px', fontWeight: 400 }}>
                        {(holidaysByYear[currentYear] ?? []).map(h => (
                          <span key={h.id} style={{ display: 'block', fontSize: '13px' }}>
                            {formatDmy(h.start_date)}
                            {h.end_date && h.end_date !== h.start_date ? `-${formatDmy(h.end_date)}` : ''}{' '}
                            {h.name}
                          </span>
                        ))}
                      </span>
                    )}
                  </button>
                </>
              ) : (
                <>
                  {/* [13] Dropdown "Chọn ngày nghỉ bù" — BS-024-R10, chỉ hiện
                      khoản status='available' (backend đã lọc sẵn). */}
                  <div className={styles.formGroup}>
                    <label className={styles.label} id="comp-balance-label">Chọn ngày nghỉ bù</label>
                    {compBalances.length === 0 ? (
                      <p className={styles.cardDescription} data-testid="comp-balance-picker-empty">
                        {compBalanceLoaded ? 'Không có ngày nghỉ bù khả dụng' : 'Đang tải...'}
                      </p>
                    ) : (
                      <div role="radiogroup" aria-labelledby="comp-balance-label">
                        {compBalances.map(cb => (
                          <button
                            key={cb.id}
                            type="button"
                            role="radio"
                            aria-checked={compensatoryBalanceId === cb.id}
                            className={`${styles.compBalanceOption} ${compensatoryBalanceId === cb.id ? styles.compBalanceOptionSelected : ''}`}
                            onClick={() => setCompensatoryBalanceId(cb.id)}
                            data-testid={`comp-balance-option-${cb.id}`}
                            style={{ marginBottom: '8px' }}
                          >
                            {cb.earned_occasion} ({cb.remaining_days} ngày, hết hạn {formatDmy(cb.expiry_date)})
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* [14] "Số ngày sử dụng" — v0 khoá cứng = 1 (BS-024-R10). */}
                  <div className={styles.formGroup}>
                    <label htmlFor="comp-days-used" className={styles.label}>Số ngày sử dụng</label>
                    <input
                      id="comp-days-used"
                      type="number"
                      value={1}
                      disabled
                      readOnly
                      className={styles.input}
                      data-testid="comp-days-used"
                    />
                  </div>
                </>
              )}

              <div className={styles.formGroup}>
                <label htmlFor="leave-reason" className={styles.label}>
                  Lý do (không bắt buộc)
                </label>
                <textarea
                  id="leave-reason"
                  name="reason"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Nhập lý do xin nghỉ"
                  maxLength={1000}
                  className={styles.textarea}
                  data-testid="leave-reason"
                  rows={3}
                />
              </div>

              {/* BS-301 Route 6 §1 (2026-08-17) — EMPTY-BALANCE / missing-balance-data
                  gate reason. Form fields stay rendered (not hidden) per AC;
                  aria-describedby wires the reason to the button for screen readers. */}
              {submitBlockedReason && (
                <p
                  id="leave-submit-blocked-reason"
                  role="alert"
                  className={styles.statusError}
                  data-testid="leave-submit-blocked-reason"
                >
                  {submitBlockedReason}
                </p>
              )}

              <div className={styles.formActions}>
                <button
                  type="submit"
                  disabled={submitting || !!submitBlockedReason}
                  aria-disabled={submitting || !!submitBlockedReason}
                  aria-describedby={submitBlockedReason ? 'leave-submit-blocked-reason' : undefined}
                  className={styles.primaryButton}
                  data-testid="submit-leave"
                >
                  {submitting ? 'Đang gửi...' : 'Gửi'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className={styles.secondaryButton}
                >
                  Hủy
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Leave requests list — hidden on balance-only page so year numbers don't pollute balance assertions */}
        {!isBalancePage && <div className={styles.card}>
          <h2 className={styles.cardTitle}>Danh sách đơn nghỉ phép</h2>
          {loading ? (
            <p className={styles.cardDescription}>Đang tải...</p>
          ) : listError ? (
            <p className={styles.statusError} data-testid="leave-list-error">{listError}</p>
          ) : requests.length === 0 ? (
            <p className={styles.cardDescription}>
              Chưa có đơn nghỉ phép nào. Nhấn "Tạo đơn" để gửi đơn mới.
            </p>
          ) : (
            <ul className={styles.requestList}>
              {requests.map(req => (
                <li
                  key={req.id}
                  className={styles.requestItem}
                  data-testid={`leave-request-${req.id}`}
                  data-status={req.status}
                >
                  <div className={styles.requestHeader}>
                    <span
                      className={styles.statusBadge}
                      data-status={req.status}
                      data-testid="leave-status-badge"
                    >
                      {STATUS_LABELS[req.status] || req.status}
                    </span>
                    {/* [5b] Badge "Đã quá hạn duyệt" — BS-024-R12: chỉ khi
                        sla_breached=true, KHÔNG áp dụng cho đơn Nghỉ bù (tự
                        động duyệt, không có SLA). */}
                    {req.sla_breached && req.leave_type !== COMPENSATORY_TYPE && (
                      <span className={styles.slaBadge} data-testid="leave-sla-badge">
                        Đã quá hạn duyệt
                      </span>
                    )}
                    {/* BS-024 annotation [5]: 4 loại thường hiện "{start} → {end}
                        ({N} ngày)"; Nghỉ bù hiện "{date} ({N} ngày)" — luôn 1
                        ngày ở v0 (BS-024-R10 [14]), không có mũi tên khoảng. */}
                    <span className={styles.requestDate}>
                      {req.leave_type === COMPENSATORY_TYPE
                        ? `${req.start_date} (${req.working_days} ngày)`
                        : `${req.start_date} → ${req.end_date} (${req.working_days} ngày)`}
                    </span>
                  </div>
                  <p className={styles.requestReason}>
                    {LEAVE_TYPE_LABELS[req.leave_type] || req.leave_type}
                    {req.is_emergency && ' — ⚡ Khẩn cấp'}
                  </p>
                  {/* BS-024 annotation [5], dòng 3 cho Nghỉ bù: đặc tả gọi đây
                      là "Từ: {earned_occasion}" (lấy từ khoản nghỉ bù đã liên
                      kết qua compensatory_balance_id). KHÔNG render được —
                      backend _build_response() (api/v1/leave_request.py) trả
                      về LeaveRequestDTO KHÔNG có compensatory_balance_id lẫn
                      earned_occasion, nên không có gì để join. Khoản đã dùng
                      hết (remaining_days=0) cũng không còn nằm trong
                      /compensatory-leave/my-balance để đối chiếu chéo. Xem
                      ghi chú @cross-references ở đầu services/api/leaveRequest.ts
                      — đây là gap backend cần bổ sung field, không phải lỗi FE. */}
                  {req.reason && (
                    <p className={styles.requestPeer}>{req.reason}</p>
                  )}
                  <div className={styles.requestFooter}>
                    <span className={styles.requestTime}>
                      {req.sla_deadline
                        ? formatSlaDeadline(req.sla_deadline, req.is_emergency)
                        : req.created_at
                          ? new Date(req.created_at).toLocaleDateString('vi-VN')
                          : ''}
                    </span>
                    {CANCELLABLE_STATUSES.includes(req.status) && (
                      <button
                        onClick={() => handleCancel(req.id)}
                        className={styles.cancelButton}
                        data-testid={`cancel-leave-${req.id}`}
                      >
                        Hủy đơn
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>}

      </main>

      {/* web-sync checklist A.2 (2026-08-15) — shared 5-tab bottom nav */}
      <EmployeeBottomNav />
    </div>
  );
};

export default LeaveRequestModern;
